import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  Container,
  MasterProductWithMenuContainerProduct,
  MenuContainerProduct,
  MenuContainerProductAttribute,
  MenuTag,
  PosMenu
} from '@/src/types/apps/gygMenuContainerProduct'
import { getSanitisedParams } from '../gygMenuContainer'
import { stringify } from 'qs'

export interface Modifier {
  id: number
  name: string
  glCode?: string | null
  description: string
  modifierId: number

  containerId: number
  parentProductId: number
  posPlu: number
  posMenuFlowId: number | null
  productType: 'P' | 'M'
  image: string | null
  imageTop: string | null
  imageAngle: string | null
  container: Container
  linearPrices: Record<string, string>
  menuContainerProductAttributes: MenuContainerProductAttribute[]
  menuTags: MenuTag[]
  posMenus: PosMenu[]
  sections: any[]
}

interface MenuContainerProductInitialState {
  menuContainerProducts?: MenuContainerProduct[]
  menuContainerProduct?: Modifier | null
  masterProductWithMenuContainerProduct?: MasterProductWithMenuContainerProduct | null
  loading: boolean
  success: string | null
  error: string | null
  isDialogOpen: boolean
  isMasterProductDialogOpen: boolean
  meta?: any
}

const initialState: MenuContainerProductInitialState = {
  masterProductWithMenuContainerProduct: null,
  loading: false,
  success: null,
  error: null,
  isDialogOpen: false,
  isMasterProductDialogOpen: false,
  menuContainerProducts: [],
  meta: null
}
interface MenuContainerProductResponse {
  meta: any
  data: any
}

interface uploadMenuContainersProductImagesResponse {
  data: any
}

export const saveMenuContainerProductTags = createAsyncThunk<
  MenuContainerProductTagsPayload,
  MenuContainerProductTagsPayload[],
  Redux
>('gygMenuContainerProducts/saveMenuContainerProductTag', async (menuTags, { extra }) => {
  const { data: responseData } = await extra.httpClient.put(`menu-container-product-tags/bulk`, menuTags)

  return responseData
})

export interface MenuContainerProductTagsPayload {
  menuContainerPosPlu: number
  menuContainerProductPosPlu: number
  posMenus: number[]
  tags: string[]
}

export const getMenuContainersProductsListByMasterProduct = createAsyncThunk<
  MenuContainerProductResponse,
  number,
  Redux
>('gygMenuContainerProducts/getMenuContainersProductsByMasterProduct', async (masterProductId, { extra }) => {
  const { data: responseData } = await extra.httpClient.get(`menu-container-products/master-product/${masterProductId}`)

  return responseData
})

export const getMenuContainersProductsWithoutMasterProduct = createAsyncThunk<MenuContainerProductResponse, any, Redux>(
  'gygMenuContainerProducts/getMenuContainersProductsWithoutMasterProduct',
  async (params, { extra }) => {
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

    const { data: responseData } = await extra.httpClient.get(
      `menu-container-products/without-master-product?${urlParams}`
    )

    return responseData
  }
)

export const getMenuContainerProductFull = createAsyncThunk<MenuContainerProductResponse, number, Redux>(
  'gygMenuContainerProducts/getMenuContainerProductFull',
  async (menuContainerProductId, { extra }) => {
    const { data: responseData } = await extra.httpClient.get(`menu-container-products/${menuContainerProductId}/full`)

    return responseData
  }
)

export const uploadMenuContainerProductImages = createAsyncThunk<
  uploadMenuContainersProductImagesResponse,
  FormData,
  Redux
>('gygMenuContainerProducts/uploadMenuContainerProductImages', async (payload, { extra }) => {
  const { data: responseData } = await extra.httpClient.post(`menu-container-products/images`, payload)

  return responseData
})

// Create the function for patching the product name and description
export const updateMenuContainerProduct = createAsyncThunk<
  any,
  { id: number; name: string; description: string },
  Redux
>('gygMenuContainerProducts/patchMenuContainerProduct', async (payload, { extra }) => {
  const { data: responseData } = await extra.httpClient.patch(`menu-container-products/${payload.id}`, {
    name: payload.name,
    description: payload.description
  })

  return responseData
})

const gygMenuContainerProductsSlice = createSlice({
  name: 'gygMenuContainerProducts',
  initialState,
  reducers: {
    setDialogOpen: (state, action) => {
      state.isDialogOpen = action.payload
    },
    setIsMasterProductDialogOpen: (state, action) => {
      state.isMasterProductDialogOpen = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMenuContainersProductsListByMasterProduct.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getMenuContainersProductsListByMasterProduct.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.masterProductWithMenuContainerProduct = action.payload.data
      })
      .addCase(getMenuContainersProductsListByMasterProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers product'
      })
      .addCase(getMenuContainersProductsWithoutMasterProduct.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getMenuContainersProductsWithoutMasterProduct.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainerProducts = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getMenuContainersProductsWithoutMasterProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers product'
      })
      .addCase(getMenuContainerProductFull.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getMenuContainerProductFull.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainerProduct = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getMenuContainerProductFull.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers product'
      })
      .addCase(uploadMenuContainerProductImages.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(uploadMenuContainerProductImages.fulfilled, state => {
        state.loading = false
        state.success = 'uploaded'
      })
      .addCase(uploadMenuContainerProductImages.rejected, (state, action) => {
        state.loading = false
        state.success = null
        state.error = action.error.message ?? 'Failed to upload images'
      })
      .addCase(updateMenuContainerProduct.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(updateMenuContainerProduct.fulfilled, state => {
        state.loading = false
        state.success = 'updated'
      })
      .addCase(updateMenuContainerProduct.rejected, (state, action) => {
        state.loading = false
        state.success = null
        state.error = action.error.message ?? 'Failed to update product'
      })
  }
})

export const { setDialogOpen, setIsMasterProductDialogOpen } = gygMenuContainerProductsSlice.actions

export default gygMenuContainerProductsSlice.reducer
